
import { Link, useLocation, useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import {
  LayoutDashboard,
  Share2,
  BarChart3,
  Globe,
  Settings,
  Users,
  Sliders,
  LogOut,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";

interface NavItem {
  label: string;
  href?: string;
  icon: React.ReactNode;
  children?: { label: string; href: string; icon: React.ReactNode }[];
  roles?: string[];
}

const navItems: NavItem[] = [
  {
    label: "Přehled",
    href: "/dashboard",
    icon: <LayoutDashboard className="h-4 w-4" />,
  },
  {
    label: "Sociální sítě",
    href: "/dashboard/social",
    icon: <Share2 className="h-4 w-4" />,
    children: [
      {
        label: "Facebook",
        href: "/dashboard/social/facebook",
        icon: <span className="h-4 w-4 text-xs font-bold">FB</span>,
      },
      {
        label: "Instagram",
        href: "/dashboard/social/instagram",
        icon: <span className="h-4 w-4 text-xs font-bold">IG</span>,
      },
      {
        label: "IG Stories",
        href: "/dashboard/social/instagram/stories",
        icon: <span className="h-4 w-4 text-xs font-bold">ST</span>,
      },
    ],
  },
  {
    label: "GA4 Analytika",
    href: "/dashboard/ga4",
    icon: <BarChart3 className="h-4 w-4" />,
    children: [
      {
        label: "Návštěvnost",
        href: "/dashboard/ga4/traffic",
        icon: <Globe className="h-4 w-4" />,
      },
    ],
  },
  {
    label: "Nastavení",
    href: "/dashboard/settings/restaurants",
    icon: <Settings className="h-4 w-4" />,
    roles: ["admin"],
    children: [
      {
        label: "Uživatelé",
        href: "/dashboard/settings/users",
        icon: <Users className="h-4 w-4" />,
      },
      {
        label: "Nastavení",
        href: "/dashboard/settings/preferences",
        icon: <Sliders className="h-4 w-4" />,
      },
    ],
  },
];

export function Sidebar() {
  const pathname = useLocation().pathname;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = (label: string) => {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <aside className="w-64 border-r bg-card h-screen flex flex-col shrink-0">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <h1 className="text-xl font-bold tracking-tight text-[var(--primary)]">Con Gusto</h1>
        <p className="text-xs text-muted-foreground mt-1 tracking-widest uppercase">Analytics</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {navItems.filter((item) => !item.roles || item.roles.includes(user?.role || "")).map((item) => {
          const isActive = item.href ? pathname === item.href : false;
          const isParentActive = item.children?.some((c) => pathname.startsWith(c.href));
          const isExpanded = expanded[item.label];
          const rowClass = cn(
            "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full text-left",
            isActive || isParentActive
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          );

          return (
            <div key={item.label}>
              {item.children && !item.href ? (
                <button type="button" onClick={() => toggleExpand(item.label)} className={cn(rowClass, "cursor-pointer")}>
                  {item.icon}
                  <span className="flex-1">{item.label}</span>
                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
              ) : (
                <div className="flex items-center">
                  <Link to={item.href!} className={cn(rowClass, "flex-1")}>
                    {item.icon}
                    {item.label}
                  </Link>
                  {item.children && (
                    <button onClick={() => toggleExpand(item.label)} className="p-1 rounded hover:bg-accent cursor-pointer">
                      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </button>
                  )}
                </div>
              )}

              {item.children && isExpanded && (
                <div className="mt-1 rounded-md bg-muted/50 py-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className={cn(
                        "flex items-center gap-3 pl-6 pr-3 py-1.5 text-sm transition-colors",
                        pathname === child.href
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )}
                    >
                      {child.icon}
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name || "..."}</p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.role === "admin" ? "Admin" : user?.role === "operator" ? "Provozovatel" : "Social"}
            </p>
          </div>
          <button className="p-1.5 rounded-md hover:bg-accent cursor-pointer" onClick={async () => { await logout(); navigate("/login"); }} title="Odhlásit se">
            <LogOut className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    </aside>
  );
}
